<template>
  <div>
    <div v-if="values.data" class="filter-title">{{ values.name }}</div>
    <v-card-text class="pa-0">
      <v-text-field
        v-if="searchable"
        v-model="search"
        hide-details
        class="mb-1 rounded-0"
        prepend-inner-icon="mdi-magnify"
        solo
        dense
        label="Search here..."
        type="text"
        clearable
        light
        @keyup="searchQueryFacet()"
      />
      <div v-if="values.data && values.data.length && listToShow.length">
        <div v-for="item in listToShow" :key="item.value">
          <label
            v-if="singleSelect"
            class="d-flex align-center"
            :title="item.value"
            style="cursor: pointer;"
            @click="singleValueSelection(item.value)"
          >
            <span
              v-if="item.value"
              :class="getSingleFieldSelected === item.value ? 'ma-1 font-weight-bold text-truncate' : 'ma-1 font-weight-light font-weight-thin text-truncate'"
            >
              {{
                item.value
              }}
            </span>
            <v-chip class=" ml-auto" small pill>{{ item.count }}</v-chip>
          </label>
          <label
            v-else
            class="d-flex align-center"
            :title="item.value"
            style="cursor: pointer;"
          >
            <input
              class="ma-1"
              type="checkbox"
              :checked="isChecked(item)"
              @click="multiValueSelection($event, item)"
            >
            <span
              v-if="item.value"
              :class="getMultiFieldSelected.includes(item.value) ? 'ma-1 font-weight-bold text-truncate' : 'ma-1 font-weight-light font-weight-thin text-truncate'"
            >
              {{
                item.value
              }}
            </span>
            <v-chip class=" ml-auto" small pill>{{ item.count }}</v-chip>
          </label>
        </div>
        <v-chip v-if="showShowMoreButton" class="ma-1 font-weight-medium" small @click="listShowMore()">
          Voir plus
        </v-chip>
        <v-chip v-if="showShowLessButton" class="ma-1 font-weight-medium" small @click="listShowLess()">
          Show
          Less
        </v-chip>
      </div>
      <div v-else>
        Aucun résultat trouvé ...
      </div>
    </v-card-text>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { useStore } from 'vue2-helpers/vuex';

import { getGraphqlSchema } from '../../graphql/index';

// eslint-disable-next-line no-undef
const props = defineProps({
  attribute: {
    type: String,
    required: true
  },
  recordsQuery: {
    type: String,
    default: ''
  },
  values: {
    type: Object,
    required: true
  },
  limit: {
    type: Number,
    default: 10
  },
  showMore: {
    type: Boolean,
    default: false
  },
  showMoreLimit: {
    type: Number,
    default: 20
  },
  sortBy: {
    type: Array,
    default: () => []
  },
  searchable: {
    type: Boolean,
    default: false
  },
  nestedFacet: {
    type: Boolean,
    default: false
  },
  singleSelect: {
    type: Boolean,
    default: false
  },
  filterToggle: {
    type: Function,
    required: true
  },
  selectedFilters: {
    type: Object,
    default: () => {}
  },
  isRaw: {
    type: Function,
    default: () => {}
  },
  missing: {
    type: String,
    default: null
  }

});

const store = useStore();

const facetSearchedList = ref([]);

// Use for Single Selected
const getSingleFieldSelected = computed(() => {
  if (props.selectedFilters[props.attribute]) return props.selectedFilters[props.attribute][0];
  return null;
});

// Use for Multi Selected
const getMultiFieldSelected = computed(() => props.selectedFilters[props.attribute] || []);

const howManyToShow = ref(props.limit);

const search = ref('');

const areAllShowing = computed(() => {
  if (facetSearchedList.value && facetSearchedList.value.length) {
    return props.values.data && howManyToShow.value >= facetSearchedList.value.length;
  }
  return props.values.data && howManyToShow.value >= props.values.data.length;
});

const listShowMore = () => {
  howManyToShow.value += props.showMoreLimit;
};

const showShowMoreButton = computed(() => props.showMore && !areAllShowing.value);

const showShowLessButton = computed(() => howManyToShow.value > props.limit && !areAllShowing.value);

const listShowLess = () => {
  const howMany = howManyToShow.value - props.showMoreLimit;
  if (howMany <= props.limit) {
    howManyToShow.value = props.limit;
    return;
  }
  howManyToShow.value = howMany;
};

const listToShow = computed(() => {
  if (search.value && search.value.length > 0) {
    return facetSearchedList.value;
  }
  return props.values.data.slice(0, howManyToShow.value);
});

const isChecked = item => {
  if (props.selectedFilters[props.attribute]) {
    return props.selectedFilters[props.attribute].includes(item.value);
  }
  return false;
};

const singleValueSelection = value => props.filterToggle(props.attribute, value);

const multiValueSelection = (_, item) => props.filterToggle(props.attribute, item.value);

const searchVariables = computed(() => {
  const aggregations = {};

  const filter = [{
    terms: {
      workspace: [
        store.getters.workspace.code
      ]
    }
  }];

  Object.entries(props.selectedFilters).filter(([facetKey, values]) => {
    if (values.length) {
      const fieldName = props.isRaw(facetKey) ? `${facetKey}.raw` : facetKey;
      filter.push({ terms: { [fieldName]: values } });
    }
  });

  const simpleQuery = {
    simple_query_string: {
      query: `${search.value}*`,
      fields: [props.attribute]
    }
  };

  // If it does a search is beacause is .raw
  const aggsTerms = {
    field: `${props.attribute}.raw`,
    size: 100,
    order: { _count: 'desc' }
  };

  // Add the missing property
  if (props.missing) {
    aggsTerms.missing = props.missing;
  }

  const aggs = { [`${props.attribute}|${props.values.name}`]: { terms: aggsTerms } };

  // Aggregation Query
  aggregations[`${props.attribute}|${props.values.name}`] = {
    filter: {
      bool: {
        filter: [],
        should: [],
        must: [],
        must_not: []
      }
    },
    aggs: {}
  };

  const facetFilters = filter.filter(f => Object.keys(f.terms)[0] !== `${props.attribute}.raw`);
  const { searchRestrictionsFilters } = store.getters;

  const input = {
    input: {
      size: 0,
      query: {
        bool: {
          // initialize query with user restrictions
          filter: facetFilters,
          must: searchRestrictionsFilters.query?.bool?.must || [],
          must_not: searchRestrictionsFilters.query?.bool?.must_not || []
        }
      },
      aggregations: aggregations
    }
  };

  if (props.recordsQuery) {
    input.input.query.bool.must.push({
      simple_query_string: {
        query: `${props.recordsQuery}*`,
        fields: store.state.searchFields
      }
    });
  }

  // for when the field is declared on openSearch as a "nested" type
  if (props.nestedFacet) {
    // Add query in a nested object to specify when we search a nested field
    aggregations[`${props.attribute}|${props.values.name}`].filter.bool.must.push({
      nested: {
        path: props.attribute.split('.')[0],
        query: { ...simpleQuery }
      }
    });

    aggregations[`${props.attribute}|${props.values.name}`].aggs = {
      [`${props.attribute}|${props.values.name}`]: {
        nested: { path: props.attribute.split('.')[0] },
        aggs: aggs
      }
    };
  } else {
    const aggregationsQueryFilters = {
      bool: {
        should: [
          { bool: { must: [simpleQuery] } }
        ]
      }
    };

    // Allways show missing values if exists
    if (props.missing) {
      aggregationsQueryFilters.bool.should.push({ bool: { must_not: { exists: { field: props.attribute } } } });
    }

    aggregations[`${props.attribute}|${props.values.name}`].filter.bool.filter.push(aggregationsQueryFilters);

    aggregations[`${props.attribute}|${props.values.name}`].aggs = { ...aggs };
  }

  return input;
});

const enableFacetSearch = ref(false);

const searchQueryFacet = () => {
  if (search.value.length > 0) {
    enableFacetSearch.value = true;
    return;
  }
  enableFacetSearch.value = false;
};

const { onResult } = useQuery(
  getGraphqlSchema('openSearch'),
  searchVariables,
  { enabled: enableFacetSearch, fetchPolicy: 'cache-first' }
);

onResult(r => {
  if (r.loading) {
    return;
  }

  // FIX clear search should query all *
  // normalize searched value
  const searchValue = search.value ? search.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';

  const results = JSON.parse(JSON.stringify(r.data.search.facets[props.attribute].data.filter(x => {
    const resultValue = x.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (searchValue && resultValue.includes(searchValue)) {
      return true;
    }

    return false;
  }))).slice(0, howManyToShow.value);

  facetSearchedList.value = results;
});

</script>
